import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createUser, fetchUser, updateUser } from '../../../api/UsersAPI';
import { getRoles } from '../../../api/RolesAPI';
import { handleApiError } from '../../../api/risapi';
import { capitalizeFirstLetter } from '../../../commonfunctions';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';

const UsersForm = ({ formType }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [currentUser, setCurrentUser] = useState({
        name: '',
        phone_number: '',
        password: '',
        password_confirmation: '',
        profile_pic_path: null,
        role_ids: [],
        date_of_birth: '',
        blood_group: '',
        member_type: 'playing',
        status: 'active' // Status field added to state
    });
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState(new Set());
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    const isEditMode = formType === 'edit';
    const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

    useEffect(() => {
        getRoles({ paginate: false }, (response) => {
            if (response.roles) setRoles(response.roles.data || response.roles);
        });
    }, []);

    useEffect(() => {
        if (isEditMode && id) {
            fetchUser(id, (response) => {
                if (response.user) {
                    setCurrentUser({
                        ...response.user,
                        member_type: response.user.member_type || 'playing',
                        status: response.user.status || 'active'
                    });
                    setSelectedRoles(new Set(response.user.role_ids));
                }
            });
        }
    }, [id, isEditMode]);

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'profile_pic_path' && files) {
            setCurrentUser(prev => ({ ...prev, [name]: files[0] }));
        } else {
            setCurrentUser(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRoleChange = (roleId, isChecked) => {
        setSelectedRoles(prev => {
            const newSelected = new Set(prev);
            if (isChecked) newSelected.add(roleId);
            else newSelected.delete(roleId);
            return newSelected;
        });
    };

    const handleSubmit = (exitForm) => {
        setIsLoading(true);
        setErrors({});
        const dataToSend = { ...currentUser, role_ids: Array.from(selectedRoles) };
        const apiCall = isEditMode ? updateUser : createUser;
        apiCall(dataToSend, (response) => {
            setIsLoading(false);
            if (response.user) {
                const message = `User ${isEditMode ? 'updated' : 'created'} successfully!`;
                if (exitForm) {
                    navigate('/club_portal/users', { state: { message } });
                } else {
                    setToaster({ type: 'success', heading: 'Success', message });
                    setShowToaster(true);
                    setCurrentUser({
                        name: '', phone_number: '', password: '', password_confirmation: '',
                        profile_pic_path: null, role_ids: [], date_of_birth: '',
                        blood_group: '', member_type: 'playing', status: 'active'
                    });
                    setSelectedRoles(new Set());
                }
            } else {
                handleApiError(response, setErrors);
            }
        });
    };

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title={isEditMode ? 'Edit User' : 'Create User'}>
                     <li><a className="f-s-14 f-w-500" href="#">Access Control</a></li>
                     <li><a className="f-s-14 f-w-500" href="/club_portal/users">Users</a></li>
                     <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                </AppBreadCrumb>
                <div className="card">
                    <div className="card-header"><h5>{isEditMode ? `Editing: ${currentUser.name}` : 'New Member Details'}</h5></div>
                    <div className="card-body">
                        <form className="app-form">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Full Name *</label>
                                    <input className="form-control" placeholder='Enter club user full name' name="name" value={currentUser.name || ''} onChange={handleInputChange} />
                                    {errors.name && <div className="text-danger small">{errors.name}</div>}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Phone Number *</label>
                                    <input className="form-control" name="phone_number" placeholder='Enter club user phone number' value={currentUser.phone_number || ''} onChange={handleInputChange} />
                                    {errors.phone_number && <div className="text-danger small">{errors.phone_number}</div>}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Password {isEditMode ? '(Optional)' : '*'}</label>
                                    <input className="form-control" type="password" placeholder='Enter club user password' name="password" value={currentUser.password || ''} onChange={handleInputChange} />
                                    {errors.password && <div className="text-danger small">{errors.password}</div>}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Confirm Password</label>
                                    <input className="form-control" type="password" placeholder='Confirm club user password' name="password_confirmation" value={currentUser.password_confirmation || ''} onChange={handleInputChange} />
                                </div>
                                 <div className="col-md-6 mb-3">
                                    <label className="form-label">Date of Birth</label>
                                    <input className="form-control" type="date" name="date_of_birth" value={currentUser.date_of_birth || ''} onChange={handleInputChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Blood Group</label>
                                    <select className="form-select" name="blood_group" value={currentUser.blood_group || ''} onChange={handleInputChange}>
                                        <option value="">Select Blood Group</option>
                                        {bloodGroupOptions.map(group => (
                                            <option key={group} value={group}>{group}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Member Type *</label>
                                    <div className="d-flex">
                                        <div className="form-check me-3">
                                            <input className="form-check-input" type="radio" name="member_type" id="playing" value="playing" checked={currentUser.member_type === 'playing'} onChange={handleInputChange} />
                                            <label className="form-check-label" htmlFor="playing">Playing Member</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="member_type" id="non-playing" value="non-playing" checked={currentUser.member_type === 'non-playing'} onChange={handleInputChange} />
                                            <label className="form-check-label" htmlFor="non-playing">Non-Playing Member</label>
                                        </div>
                                    </div>
                                </div>
                                {isEditMode && (
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Status *</label>
                                        <div className="d-flex">
                                            <div className="form-check me-3">
                                                <input className="form-check-input" type="radio" name="status" id="active" value="active" checked={currentUser.status === 'active'} onChange={handleInputChange} />
                                                <label className="form-check-label" htmlFor="active">Active</label>
                                            </div>
                                            <div className="form-check">
                                                <input className="form-check-input" type="radio" name="status" id="disabled" value="disabled" checked={currentUser.status === 'disabled'} onChange={handleInputChange} />
                                                <label className="form-check-label" htmlFor="disabled">Disabled</label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="col-12 mb-3">
                                    <label className="form-label">Assign Roles</label>
                                    <div className="permission-grid">
                                        {roles.map(role => (
                                            <div key={role.id} className="form-check">
                                                <input className="form-check-input" type="checkbox" id={`role-${role.id}`} checked={selectedRoles.has(role.id)} onChange={(e) => handleRoleChange(role.id, e.target.checked)} />
                                                <label className="form-check-label" htmlFor={`role-${role.id}`}>{role.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">Profile Picture</label>
                                    <input className="form-control" type="file" name="profile_pic_path" onChange={handleInputChange} />
                                </div>
                                 <div className="col-12 text-end">
                                    {!isEditMode ? (
                                        <>
                                            <button type="button" className="btn btn-outline-primary me-2 btn-sm" onClick={() => handleSubmit(false)}>Save + Next</button>
                                            <button type="button" className="btn btn-primary me-2 btn-sm" onClick={() => handleSubmit(true)}>Save + Exit</button>
                                        </>
                                    ) : (
                                        <>
                                            <button type="button" className="btn btn-primary me-2 btn-sm" onClick={() => handleSubmit(true)} disabled={isLoading}>{isLoading ? 'Updating...' : 'Update User'}</button>
                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => navigate('/club_portal/users')}>Cancel</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <style>{`.permission-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }`}</style>
            </div>
        </main>
    );
};

export default UsersForm;

